import { Box, Flex, Group, Stack, Text, createStyles } from '@mantine/core';
import Button, { ButtonTypes } from '@/components/Buttons/Button';
import { ITechstack, ITechstackDetail } from '@/types/techstack';

import DMText from '@/components/DMText';
import { FRAMEWORK_LOGO_ENDPOINT } from '@/config/endpoint';
import Image from 'next/image';
import { imageOpt } from '@/utils/images';
import { isDark } from '@/utils/utils';
import themeVariables from '@/styles/themeVariables';

const useTechstackDetailHeaderStyle = createStyles((theme) => ({
  headerWrapper: {
    width: '100%',
    borderRadius: 20,
    padding: 36,
    [theme.fn.smallerThan('sm')]: {
      flexDirection: 'column',
      padding: 18,
    },
  },

  title: {
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: -0.6,
    lineHeight: 1.4,
    [theme.fn.smallerThan('sm')]: {
      fontsize: 20,
    },
  },

  subtitle: {
    fontSize: 13,
    fontWeight: 600,
    [theme.fn.smallerThan('sm')]: {
      fontSize: 12,
    },
  },

  description: {
    fontSize: 15,
    [theme.fn.smallerThan('sm')]: {
      fontSize: 14,
    },
  },

  headerStatistics: {
    fontSize: 16,
    letterSpacing: 0.1,
    gap: 18,
    [theme.fn.smallerThan('sm')]: {
      fontSize: 14,
      flexDirection: 'column',
      gap: 0,
    },
  },

  logoWrapper: {
    borderRadius: '50%',
    backgroundColor: 'white',
    strokeWidth: '1px',
    stroke: 'rgba(0, 0, ,0, 0.05)',
    overflow: 'hidden',
  },

  mapWrapper: {
    position: 'relative',
    margin: '0px 0px 0px auto',
    [theme.fn.smallerThan('sm')]: {
      margin: '30px 0px 0px 0px',
    },
  },

  imageOverlay: {
    position: 'absolute',
    top: 0,
    width: '100%',
    borderRadius: '20px 20px 0px 0px',
    height: 88,
    background: 'linear-gradient(0deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 100%)',
    opacity: 0.4,
    mixBlendMode: 'multiply',
  },
}));

const TechstackDetailHeader = ({ data }: { data: ITechstack }) => {
  const { dark } = isDark();
  const { classes } = useTechstackDetailHeaderStyle();

  const { techstack } = data as ITechstack;

  const {
    id,
    name,
    website_url,
    category,
    description,
    github_url,
    github_stars,
    stackoverflow_questions,
  } = (techstack as ITechstackDetail) || {};

  return (
    <Flex
      className={classes.headerWrapper}
      bg={dark ? 'none' : '#00000005'}
      sx={{
        border: dark ? `1px solid ${themeVariables.line.dark}` : 'none',
      }}
    >
      <Stack spacing={10}>
        <Group pb={20} spacing={20} noWrap>
          <Box className={classes.logoWrapper}>
            <Image
              width={64}
              height={64}
              src={imageOpt(`${FRAMEWORK_LOGO_ENDPOINT}/${id}.png`, 150)}
              alt="logo"
            />
          </Box>
          <Stack spacing={0}>
            <Text
              className={classes.subtitle}
              color={
                dark
                  ? themeVariables.colors.blue200
                  : themeVariables.colors.blue300
              }
            >
              {category}
            </Text>
            <DMText
              className={classes.title}
              dColor={themeVariables.darkMode.normal}
            >
              {name}
            </DMText>
          </Stack>
        </Group>
        <DMText
          className={classes.description}
          dColor={themeVariables.darkMode.alternative}
        >
          {description}
        </DMText>
        <Flex className={classes.headerStatistics}>
          {stackoverflow_questions ? (
            <DMText dColor={themeVariables.darkMode.alternative}>
              StackOverflow 질문 수:{` ${stackoverflow_questions}`}
            </DMText>
          ) : null}
          {github_stars ? (
            <DMText dColor={themeVariables.darkMode.alternative}>
              Github Stars : ★{` ${github_stars}`}
            </DMText>
          ) : null}
        </Flex>
        <Group spacing={12} pt={20}>
          <Button
            style={{
              height: 40,
              backgroundColor: '#0095FF',
              color: 'white',
            }}
            onClick={() => window.open(website_url, '_blank')}
          >
            공식 사이트 바로가기
          </Button>
          {github_url && (
            <Button
              type={ButtonTypes.gray}
              style={{ height: 40 }}
              onClick={() => window.open(github_url, '_blank')}
            >
              Github 바로가기
            </Button>
          )}
        </Group>
      </Stack>
    </Flex>
  );
};

export default TechstackDetailHeader;
